import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { AlertCircle, Bot, CheckCircle2, CornerUpRight, FolderCheck, FolderInput, Loader2, Play, RefreshCw, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const ICON = { forwarded_invoice: CornerUpRight, moved_done: FolderCheck, logged_on_file: FolderInput, assigned: UserCheck, skipped: AlertCircle, error: AlertCircle };
const TONE = {
  forwarded_invoice: "bg-amber-50 text-amber-800 border-amber-300",
  moved_done: "bg-slate-50 text-slate-700 border-slate-300",
  logged_on_file: "bg-green-50 text-green-700 border-green-300",
  assigned: "bg-sky-50 text-sky-700 border-sky-300",
  skipped: "bg-orange-50 text-orange-800 border-orange-300",
  error: "bg-red-50 text-red-700 border-red-300",
};

// What the Accounts autopilot did, newest first — plus a "Run now" button.
export function AutopilotLog({ api, settings, caseLink, onSettings }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.autopilotLog(150);
      setEntries(r.entries || []);
      if (r.settings) onSettings?.(r.settings);
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setLoading(false); }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.autopilotRun();
      const ap = r.result?.autopilot;
      toast.success(ap ? `Tick done — ${ap.evaluated} email${ap.evaluated === 1 ? "" : "s"} evaluated` : `Tick done — ${r.result?.triaged ?? 0} triaged (autopilot is off)`);
      await load();
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setRunning(false); }
  };

  const on = settings?.mode === "autopilot";
  return (
    <CardContent className="p-0 flex-1 min-h-0 flex flex-col" data-testid="autopilot-log">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50 text-[11px] shrink-0">
        <Bot className={cn("w-3.5 h-3.5", on ? "text-emerald-600" : "text-slate-400")} />
        <span className="font-medium" data-testid="autopilot-log-status">{on ? "Autopilot is on" : "Autopilot is off"}</span>
        {on && settings?.autopilot_since && <span className="text-muted-foreground">· since {new Date(settings.autopilot_since).toLocaleString("en-GB")}</span>}
        <span className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={load} disabled={loading} data-testid="autopilot-log-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={runNow} disabled={running} data-testid="autopilot-run-now">
            {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}{on ? "Run now" : "Triage now"}
          </Button>
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto divide-y">
        {entries === null && <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading…</div>}
        {entries?.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="autopilot-log-empty">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            Nothing yet — {on ? "actions appear here as new mail is handled" : "switch Autopilot on to start handling new mail"}.
          </div>
        )}
        {(entries || []).map((x) => {
          const Icon = ICON[x.action] || Bot;
          return (
            <div key={x.id} className={cn("px-3 py-2 text-xs", !x.ok && "bg-red-50/40")} data-testid={`autopilot-entry-${x.id}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-[9px] gap-1", TONE[x.action] || TONE.moved_done)}><Icon className="w-2.5 h-2.5" />{x.label}</Badge>
                <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{new Date(x.at).toLocaleString("en-GB")}</span>
              </div>
              <div className="truncate mt-1 font-medium">{x.subject}</div>
              <div className="truncate text-[11px] text-muted-foreground">{x.from_name || x.from_email}{x.from_name && x.from_email ? ` <${x.from_email}>` : ""}</div>
              <div className="text-[11px] text-slate-700 mt-0.5">{x.detail}</div>
              {x.case && caseLink && (
                <Link to={caseLink(x.case)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-indigo-700 hover:underline mt-0.5">
                  <FolderInput className="w-3 h-3" />{x.case.sf || x.case.customer_name} · {x.case.customer_name}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </CardContent>
  );
}
